import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { isClickedOutside, lockScroll, unlockScroll } from '@theme/utilities';

let riftSizeGuideInstance = 0;

class RiftSizeGuide extends HTMLElement {
  static observedAttributes = ['data-rift-target-option', 'data-rift-trigger-position'];

  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {AbortController | null} */
  #triggerAbortController = null;
  /** @type {MutationObserver | null} */
  #scopeObserver = null;
  /** @type {HTMLButtonElement | null} */
  #trigger = null;
  /** @type {HTMLElement | null} */
  #option = null;
  /** @type {HTMLAnchorElement | null} */
  #imageTrigger = null;
  /** @type {HTMLDivElement | null} */
  #dialogPortal = null;
  /** @type {string} */
  #instanceId = '';
  #attachQueued = false;

  connectedCallback() {
    if (this.#abortController) return;

    this.#abortController = new AbortController();
    this.#instanceId ||= `RiftSizeGuide-${++riftSizeGuideInstance}`;
    this.#mountQuickAddPortal();

    const controller = this.#controller;
    const guideDialog = this.#guideDialog;
    const heading = this.#heading;
    const dialogRoot = this.#dialogRoot;

    if (!controller || !guideDialog || !heading || !dialogRoot) return;

    this.#syncRuntimeIds();

    controller.addEventListener(DialogOpenEvent.eventName, this.#handleGuideOpen, {
      capture: true,
      signal: this.#abortController.signal,
    });
    controller.addEventListener(DialogCloseEvent.eventName, this.#handleGuideClose, {
      capture: true,
      signal: this.#abortController.signal,
    });
    dialogRoot.addEventListener('click', this.#handleClick, {
      signal: this.#abortController.signal,
    });
    dialogRoot.addEventListener('cancel', this.#handleCancel, {
      capture: true,
      signal: this.#abortController.signal,
    });
    dialogRoot.addEventListener('keydown', this.#handleKeyDown, {
      capture: true,
      signal: this.#abortController.signal,
    });

    document.addEventListener('shopify:block:select', this.#handleEditorSelection, {
      signal: this.#abortController.signal,
    });

    this.#observeProductScope();
    this.#queueAttachTrigger();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#scopeObserver?.disconnect();
    this.#scopeObserver = null;
    this.#removeTrigger();
    this.#closeZoomImmediately();

    const guideDialog = this.#guideDialog;
    if (guideDialog?.open) {
      unlockScroll(guideDialog);
      guideDialog.close();
    }

    this.#restoreDialogPortal();
    this.#imageTrigger = null;
  }

  attributeChangedCallback() {
    if (!this.isConnected) return;
    this.#queueAttachTrigger();
  }

  get #controller() {
    const controller = this.#dialogRoot?.querySelector('[data-rift-size-guide-controller]');
    return controller && 'showDialog' in controller && 'closeDialog' in controller ? controller : null;
  }

  get #guideDialog() {
    const dialog = this.#dialogRoot?.querySelector('[data-rift-size-guide-dialog]');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  get #zoomDialog() {
    const dialog = this.#dialogRoot?.querySelector('[data-rift-size-guide-zoom-dialog]');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  get #heading() {
    const heading = this.#dialogRoot?.querySelector('[data-rift-size-guide-heading]');
    return heading instanceof HTMLElement ? heading : null;
  }

  get #dialogRoot() {
    return this.#dialogPortal || this;
  }

  get #productScope() {
    return (
      this.closest('.product-details') ||
      this.closest('#quick-add-modal-content') ||
      this.closest('[data-product-grid-content]') ||
      this.parentElement
    );
  }

  #mountQuickAddPortal() {
    if (this.#dialogPortal || !this.closest('quick-add-dialog')) return;

    const controller = this.querySelector('[data-rift-size-guide-controller]');
    const zoomDialog = this.querySelector('[data-rift-size-guide-zoom-dialog]');
    if (!(controller instanceof HTMLElement)) return;

    const portal = document.createElement('div');
    portal.className = 'rift-size-guide__portal';
    portal.dataset.riftSizeGuidePortal = this.#instanceId;
    portal.append(controller);
    if (zoomDialog instanceof HTMLDialogElement) portal.append(zoomDialog);

    document.body.append(portal);
    this.#dialogPortal = portal;
  }

  #restoreDialogPortal() {
    const portal = this.#dialogPortal;
    if (!portal) return;

    for (const child of Array.from(portal.children)) {
      this.append(child);
    }

    portal.remove();
    this.#dialogPortal = null;
  }

  #observeProductScope() {
    const scope = this.#productScope;
    if (!scope) return;

    this.#scopeObserver = new MutationObserver(() => this.#queueAttachTrigger());
    this.#scopeObserver.observe(scope, { childList: true, subtree: true });
  }

  #queueAttachTrigger() {
    if (this.#attachQueued) return;
    this.#attachQueued = true;

    queueMicrotask(() => {
      this.#attachQueued = false;
      if (this.isConnected) {
        this.#syncRuntimeIds();
        this.#attachTrigger();
      }
    });
  }

  #syncRuntimeIds() {
    const guideDialog = this.#guideDialog;
    const heading = this.#heading;
    if (!guideDialog || !heading) return;

    heading.id = `${this.#instanceId}-heading`;
    guideDialog.id = `${this.#instanceId}-dialog`;
    guideDialog.setAttribute('aria-labelledby', heading.id);

    const zoomDialog = this.#zoomDialog;
    if (zoomDialog) zoomDialog.id = `${this.#instanceId}-zoom`;

    if (this.#trigger) this.#trigger.setAttribute('aria-controls', guideDialog.id);
  }

  #attachTrigger() {
    const scope = this.#productScope;
    const targetName = this.dataset.riftTargetOption?.trim().toLocaleLowerCase();
    if (!scope || !targetName) return this.#removeTrigger();

    const options = scope.querySelectorAll('.variant-option[data-rift-option-name]');
    const option = Array.from(options).find(
      (candidate) => candidate.getAttribute('data-rift-option-name')?.trim().toLocaleLowerCase() === targetName
    );
    const label = option?.querySelector('[data-rift-option-label]');

    if (!(option instanceof HTMLElement) || !(label instanceof HTMLElement)) return this.#removeTrigger();
    if (this.#trigger?.isConnected && this.#option === option && label.contains(this.#trigger)) return;

    this.#removeTrigger();

    const template = this.querySelector('[data-rift-size-guide-trigger-template]');
    if (!(template instanceof HTMLTemplateElement)) return;

    const trigger = template.content.firstElementChild?.cloneNode(true);
    if (!(trigger instanceof HTMLButtonElement)) return;

    const position = this.dataset.riftTriggerPosition === 'adjacent' ? 'adjacent' : 'row-end';
    trigger.classList.add(`rift-size-guide__trigger--${position}`);
    trigger.setAttribute('aria-controls', this.#guideDialog?.id || '');

    this.#triggerAbortController = new AbortController();
    trigger.addEventListener('click', this.#openGuide, { signal: this.#triggerAbortController.signal });

    option.classList.add('variant-option--has-size-guide');
    option.setAttribute('data-rift-size-guide-position', position);
    label.append(trigger);

    this.#option = option;
    this.#trigger = trigger;
  }

  #removeTrigger() {
    this.#triggerAbortController?.abort();
    this.#triggerAbortController = null;
    this.#trigger?.remove();
    this.#trigger = null;

    this.#option?.classList.remove('variant-option--has-size-guide');
    this.#option?.removeAttribute('data-rift-size-guide-position');
    this.#option = null;
  }

  #openGuide = () => {
    this.#controller?.showDialog();
  };

  #closeGuide = () => {
    if (this.#zoomDialog?.open) this.#closeZoom();
    this.#controller?.closeDialog();
  };

  #handleGuideOpen = (event) => {
    if (event.target !== this.#controller) return;
    this.#trigger?.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => this.#heading?.focus({ preventScroll: true }));
  };

  #handleGuideClose = (event) => {
    if (event.target !== this.#controller) return;
    this.#trigger?.setAttribute('aria-expanded', 'false');
    if (this.#trigger?.isConnected) this.#trigger.focus({ preventScroll: true });
  };

  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-rift-size-guide-zoom-close]')) {
      this.#closeZoom();
      return;
    }

    if (target.closest('[data-rift-size-guide-close]')) {
      this.#closeGuide();
      return;
    }

    const imageTrigger = target.closest('[data-rift-size-guide-image-trigger]');
    if (imageTrigger instanceof HTMLAnchorElement) {
      this.#imageTrigger = imageTrigger;
      this.#openZoom(event);
      return;
    }

    const zoomDialog = this.#zoomDialog;
    const zoomSurface = this.#dialogRoot?.querySelector('[data-rift-size-guide-zoom-surface]');
    if (zoomDialog?.open && zoomSurface instanceof HTMLElement && isClickedOutside(event, zoomSurface)) {
      this.#closeZoom();
    }
  };

  #handleCancel = (event) => {
    if (event.target !== this.#zoomDialog) return;
    event.preventDefault();
    this.#closeZoom();
  };

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#zoomDialog?.open) return;
    event.preventDefault();
    event.stopPropagation();
    this.#closeZoom();
  };

  #openZoom = (event) => {
    const zoomDialog = this.#zoomDialog;
    const guideDialog = this.#guideDialog;
    if (!zoomDialog || !guideDialog || zoomDialog.open) return;

    event.preventDefault();
    guideDialog.inert = true;
    lockScroll(zoomDialog);
    zoomDialog.showModal();

    const closeButton = this.#dialogRoot?.querySelector('[data-rift-size-guide-zoom-close]');
    if (closeButton instanceof HTMLElement) closeButton.focus({ preventScroll: true });
  };

  #closeZoom = () => {
    const zoomDialog = this.#zoomDialog;
    if (!zoomDialog?.open) return;

    unlockScroll(zoomDialog);
    zoomDialog.close();

    const guideDialog = this.#guideDialog;
    if (guideDialog) guideDialog.inert = false;
    if (this.#imageTrigger?.isConnected) this.#imageTrigger.focus({ preventScroll: true });
  };

  #closeZoomImmediately() {
    const zoomDialog = this.#zoomDialog;
    if (zoomDialog?.open) {
      unlockScroll(zoomDialog);
      zoomDialog.close();
    }

    const guideDialog = this.#guideDialog;
    if (guideDialog) guideDialog.inert = false;
  }

  #handleEditorSelection = (event) => {
    if (!(event.target instanceof Element) || !event.target.contains(this)) return;
    this.#queueAttachTrigger();
  };
}

if (!customElements.get('rift-size-guide')) {
  customElements.define('rift-size-guide', RiftSizeGuide);
}
